import React, { useEffect, useMemo, useState, memo } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { Icon } from "@iconify/react";

// Static import of the JSON data file.
import statesDataFile from "../../data/india-states-districts.json";

/**
 * Make the dropdown icon a stable component reference.
 * Using React.memo keeps the same component identity across parent renders,
 * preventing MUI Select from remounting the icon on each render.
 */
const DropdownIcon = memo((props) => {
  return (
    <Icon
      icon="eva:arrow-ios-downward-fill"
      width="20"
      height="20"
      {...props}
    />
  );
});

const StateDistrictFilter = ({
  onChange = () => {},
  initialState = "",
  initialDistrict = "",
  addEmptyOption = true,
  stateLabel = "State",
  districtLabel = "District",
}) => {
  // Use the statically imported data
  const [statesData] = useState(
    Array.isArray(statesDataFile) ? statesDataFile : [],
  );
  const [selectedState, setSelectedState] = useState(initialState || "");
  const [districtsForState, setDistrictsForState] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(
    initialDistrict || "",
  );

  const states = useMemo(() => statesData, [statesData]);

  // update districts list when state changes (internal only)
  useEffect(() => {
    if (!selectedState) {
      setDistrictsForState([]);
      setSelectedDistrict("");
      return;
    }

    const found = states.find(
      (s) => s.state === selectedState || s.state === String(selectedState),
    );
    const list = found && Array.isArray(found.districts) ? found.districts : [];
    setDistrictsForState(list);

    // clear selected district when state changes (internal); do NOT notify parent here
    setSelectedDistrict("");
  }, [selectedState, states]);

  // ensure initialState and initialDistrict populate UI on mount without firing onChange
  useEffect(() => {
    if (initialState) setSelectedState(initialState);
    if (initialDistrict) setSelectedDistrict(initialDistrict);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler: call parent only when user explicitly selects state
  const handleStateSelect = (newState) => {
    setSelectedState(newState || "");
    // notify parent that user selected a state (district is reset)
    onChange(newState || "", "");
  };

  // Handler: call parent only when user explicitly selects district
  const handleDistrictSelect = (newDistrict) => {
    setSelectedDistrict(newDistrict || "");
    // notify parent that user selected a district for the current state
    onChange(selectedState || "", newDistrict || "");
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        // stack on mobile (column), row on desktop
        flexDirection: "row",
        alignItems: { xs: "stretch", md: "center" },
        width: "100%",
      }}
    >
      {/* ---------- State Select ---------- */}
      <FormControl
        size="small"
        sx={{
          minWidth: 140,
          flexShrink: 0,
          backgroundColor: "var(--app-surface)",
          border: "1px solid var(--app-border)",
          borderRadius: "8px",
          height: 40,
          display: "flex",
          justifyContent: "center",
          "& .MuiInputBase-root": {
            height: 40,
            fontSize: 14,
            fontWeight: 500,
            color: "var(--app-text)",
            paddingLeft: 1,
            alignItems: "center",
          },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            paddingTop: 0,
            paddingBottom: 0,
            height: 40,
          },
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          "& .MuiSelect-icon": {
            top: "50%",
            transform: "translateY(-50%)",
          },
        }}
      >
        <Select
          labelId="state-select-label"
          IconComponent={DropdownIcon}
          value={selectedState}
          displayEmpty
          onChange={(e) => handleStateSelect(e.target.value)}
          renderValue={(value) =>
            !value ? (
              <span style={{ color: "var(--app-text)", fontSize: 15 }}>All States</span>
            ) : (
              <span style={{ fontSize: 15 }}>{value}</span>
            )
          }
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 280,
                mt: 0.5,
                borderRadius: "12px",
                border: "1px solid var(--app-border)",
                boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
                "& .MuiMenuItem-root": {
                  fontSize: 14,
                  py: 0.5,
                  fontFamily: "figtree",
                },
              },
            },
          }}
          sx={{
            width: "100%",
            pl: 1,
            pr: 1,
            height: 40,
            fontSize: 14,
            fontFamily: "figtree",
          }}
        >
          <MenuItem value="">
            <em>All States</em>
          </MenuItem>

          {states.map((s) => (
            <MenuItem key={s.state} value={s.state}>
              {s.state}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* ---------- District Select ---------- */}
      <FormControl
        size="small"
        sx={{
          minWidth: 140,
          flexShrink: 0,
          backgroundColor: "var(--app-surface)",
          border: "1px solid var(--app-border)",
          borderRadius: "8px",
          height: 40,
          display: "flex",
          justifyContent: "center",
          "& .MuiInputBase-root": {
            height: 40,
            fontSize: 14,
            fontWeight: 500,
            color: "var(--app-text)",
            paddingLeft: 1,
          },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            paddingTop: 0,
            paddingBottom: 0,
            height: 40,
          },
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          "& .MuiSelect-icon": {
            top: "50%",
            transform: "translateY(-50%)",
          },
        }}
        disabled={!selectedState}
      >
        <Select
          labelId="district-select-label"
          IconComponent={DropdownIcon}
          value={selectedDistrict}
          displayEmpty
          onChange={(e) => handleDistrictSelect(e.target.value)}
          renderValue={(value) =>
            !value ? (
              <span style={{ color: "var(--app-text)", fontSize: 15 }}>
                {" "}
                All Districts
              </span>
            ) : (
              <span style={{ fontSize: 15 }}>{value}</span>
            )
          }
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 280,
                mt: 0.5,
                borderRadius: "12px",
                border: "1px solid var(--app-border)",
                boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
                "& .MuiMenuItem-root": { fontSize: 14, py: 0.5 },
              },
            },
          }}
          sx={{ width: "100%", pl: 1, pr: 1, height: 40, fontSize: 14 }}
        >
          <MenuItem value="">
            <em>All Districts</em>
          </MenuItem>

          {districtsForState.map((d) => (
            <MenuItem key={d} value={d}>
              {d}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default StateDistrictFilter;
